import { isApiError } from '@/api';
import { FileText } from '@/design-system/icons';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { EditFileSidebar } from './EditFileSidebar';
import { DocumentFile, DocumentPermission } from '../../types';
import { useDocumentFile } from '../../hooks';

interface FileDetailPanelProps {
  file: DocumentFile | null;
  permission: DocumentPermission;
  onFileRemoved: (fileId: string) => void;
  onFileUpdated?: (fileId: string, name: string, category: number) => void;
}

/**
 * @component FileDetailPanel
 * @description Orchestrator for the file detail side panel. Fetches the full file detail
 * on selection and delegates rendering to AdminFileDetailPanel or ReadOnlyFileDetailPanel
 * based on permission. Handles empty, loading, error, and not-found states.
 *
 * @props
 *   file (DocumentFile | null, required) - The selected file from the explorer list, or null when nothing is selected.
 *   permission (DocumentPermission, required) - Whether the current user is 'admin' or 'read'.
 *   onFileRemoved ((fileId: string) => void, required) - Called when the file is deleted or moved; closes the panel.
 *   onFileUpdated ((fileId, name, category) => void, optional) - Called on successful metadata save to sync the list row.
 */
export function FileDetailPanel({
  file,
  permission,
  onFileRemoved,
  onFileUpdated,
}: FileDetailPanelProps) {
  const { t } = useTranslation();

  const { data: fileDetail, isLoading, isError, error } = useDocumentFile(
    file?.id ?? null
  );

  const isNotFound =
    isError && isApiError(error) && error.status === 404;

  // When the file is not found on the server, remove it from the list
  useEffect(() => {
    if (isNotFound && file?.id) {
      onFileRemoved(file.id);
    }
  }, [isNotFound, file?.id, onFileRemoved]);

  if (!file) {
    return (
      <div className="flex h-full min-h-80 flex-col items-center justify-center gap-4 rounded-xl border border-dashed border-border bg-surface p-6 text-center text-onsurface">
        <div className="flex size-14 items-center justify-center rounded-full bg-primary/10 text-primary">
          <FileText className="size-7" />
        </div>
        <div className="space-y-2">
          <h2 className="text-lg font-semibold">{t('documents.detail.emptyTitle')}</h2>
          <p className="text-sm text-onsurface/70">{t('documents.detail.emptyDescription')}</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex h-full min-h-80 flex-col items-center justify-center gap-4 rounded-xl border border-border bg-surface p-6">
        <div className="border-primary size-8 animate-spin rounded-full border-2 border-t-transparent" />
        <p className="text-sm text-onsurface/70">{t('documents.detail.loading')}</p>
      </div>
    );
  }

  if (isError || !fileDetail) {
    return (
      <div className="flex h-full min-h-80 flex-col items-center justify-center gap-4 rounded-xl border border-border bg-surface p-6 text-center">
        <p className="text-sm text-onsurface/70">{t('documents.detail.notFound')}</p>
      </div>
    );
  }

  if (permission === 'admin') {
    return (
      <EditFileSidebar
        key={file.id}
        fileDetail={fileDetail}
        onFileRemoved={onFileRemoved}
        onFileUpdated={onFileUpdated ?? (() => { })}
      />
    );
  }
}
